"use client";

import { useState, useEffect } from "react";
import { X, Eye, EyeOff } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ROLES } from "@/lib/constants/roles";
import { usePathname } from "next/navigation";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  roles: Array<{ id: number; name: string }>;
  venues: Array<{ id: number; name: string }>;
  statuses: Array<{ id: number; name: string }>;
  /**
   * Saves the form instead of the super admin users API, e.g. a booking
   * portal server action that applies its own access checks.
   */
  onSubmit?: (data: any) => Promise<{ success: boolean; error?: string }>;
  user?: {
    id: number;
    name: string | null;
    email?: string;
    user_id: string;
    role_id: number;
    venue_id: number | null;
    status_id: number;
    statuses?: { name: string };
    user_venue_permissions?: Array<{
      venue_id: number;
      venues?: { id: number; name: string };
    }>;
  };
}

export default function UserModal({
  isOpen,
  onClose,
  onSuccess,
  roles,
  venues,
  statuses,
  onSubmit,
  user,
}: UserModalProps) {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVenueIds, setSelectedVenueIds] = useState<number[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [setPasswordManually, setSetPasswordManually] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  const isEditMode = !!user;
  // Since we're in /superadmin/users, the user must be a superadmin
  const isSuperAdmin = pathname?.startsWith("/superadmin");

  // Reset form function
  const resetForm = () => {
    setSelectedVenueIds([]);
    setSelectedRole("");
    setSetPasswordManually(false);
    setShowPassword(false);
    setChangePassword(false);
    setError(null);
    // Reset form inputs
    const form = document.getElementById("user-form") as HTMLFormElement;
    if (form) {
      form.reset();
    }
  };

  // Initialize form when modal opens or user data changes
  useEffect(() => {
    if (isOpen) {
      if (user) {
        // Edit mode - populate with user data
        console.log("UserModal - User data:", user);
        console.log(
          "UserModal - Venue permissions:",
          user.user_venue_permissions,
        );

        if (
          user.user_venue_permissions &&
          user.user_venue_permissions.length > 0
        ) {
          const venueIds = user.user_venue_permissions.map((p) => p.venue_id);
          console.log(
            "UserModal - Setting venue IDs from permissions:",
            venueIds,
          );
          setSelectedVenueIds(venueIds);
        } else if (user.venue_id) {
          console.log(
            "UserModal - Setting venue ID from primary venue:",
            user.venue_id,
          );
          setSelectedVenueIds([user.venue_id]);
        } else {
          console.log("UserModal - No venue data found");
          setSelectedVenueIds([]);
        }
        setSelectedRole(user.role_id?.toString() || "");
        setChangePassword(false); // Reset password change checkbox
      } else {
        // Create mode - reset form
        resetForm();
      }
      setError(null);
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleVenueToggle = (venueId: number) => {
    setSelectedVenueIds((prev) => {
      if (prev.includes(venueId)) {
        return prev.filter((id) => id !== venueId);
      } else {
        return [...prev, venueId];
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const data: any = {};

    // Convert FormData to object using forEach (compatible with all TypeScript targets)
    formData.forEach((value, key) => {
      if (key === "roleId" || key === "statusId") {
        data[key.replace("Id", "_id")] = value
          ? parseInt(value.toString())
          : null;
      } else {
        data[key] = value;
      }
    });

    // Add venue_ids for multi-venue support
    data.venue_ids = selectedVenueIds;

    // Keep venue_id for backward compatibility (use first selected venue)
    data.venue_id = selectedVenueIds.length > 0 ? selectedVenueIds[0] : null;

    // Add password setup preference
    data.setPasswordManually = setPasswordManually;

    // Add change password flag for edit mode
    if (isEditMode && changePassword) {
      data.changePassword = changePassword;
    }

    if (onSubmit) {
      try {
        const result = await onSubmit(data);
        if (!result.success) {
          setError(result.error || "An error occurred");
        } else {
          if (!isEditMode) resetForm();
          onClose();
          onSuccess();
        }
      } catch {
        setError("An unexpected error occurred");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      const url = isEditMode
        ? `/api/superadmin/users/${user.id}`
        : "/api/superadmin/users";

      const method = isEditMode ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || "An error occurred");
      } else {
        // Reset form for create mode
        if (!isEditMode) {
          resetForm();
        }
        onClose();
        onSuccess();
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  // Determine if venue selection should be shown based on role
  const showVenueSelection = () => {
    const roleId = parseInt(selectedRole);
    // Hide venue selection for super admin. Hosts get venues too: bar staff
    // can only pick a host who has access to the booking's venue.
    return roleId !== ROLES.SUPER_ADMIN;
  };

  // Determine if password field should be shown based on role
  const showPasswordField = () => {
    const roleId = parseInt(selectedRole);
    // Hide password field for host role since they cannot login
    return roleId !== ROLES.HOST;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-background border rounded-lg shadow-lg w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {isEditMode ? "Edit user" : "Add new user"}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-md transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          id="user-form"
          onSubmit={handleSubmit}
          className="p-4 overflow-y-auto max-h-[calc(90vh-8rem)]"
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email {!isEditMode && <span className="text-red-500">*</span>}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required={!isEditMode}
                disabled={isEditMode}
                defaultValue={user?.email}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-muted disabled:cursor-not-allowed"
                placeholder="user@example.com"
              />
              {isEditMode && (
                <p className="text-xs text-muted-foreground mt-1">
                  Email cannot be changed after creation
                </p>
              )}
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                defaultValue={user?.name || ""}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="John Doe"
              />
            </div>

            {/* Password change for super admins in edit mode - hide for host role */}
            {isEditMode && isSuperAdmin && showPasswordField() && (
              <>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="changePassword"
                      checked={changePassword}
                      onCheckedChange={(checked) =>
                        setChangePassword(checked as boolean)
                      }
                    />
                    <Label
                      htmlFor="changePassword"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Change password
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    Set a new password for this user
                  </p>
                </div>

                {changePassword && (
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium mb-1"
                    >
                      New password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        required={changePassword}
                        placeholder="Enter new password"
                        className="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Password must be at least 6 characters long
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Password field for new users - hide for host role */}
            {!isEditMode && showPasswordField() && (
              <>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="setPasswordManually"
                      checked={setPasswordManually}
                      onCheckedChange={(checked) =>
                        setSetPasswordManually(checked as boolean)
                      }
                    />
                    <Label
                      htmlFor="setPasswordManually"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Set password manually
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    {setPasswordManually
                      ? "You will set the password for the user"
                      : "A welcome email will be sent to the user with a link to the login page, where they sign in with a one-time code (no password needed)."}
                  </p>
                </div>

                {setPasswordManually && (
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium mb-1"
                    >
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        required={setPasswordManually}
                        placeholder="Enter a secure password"
                        className="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Password must be at least 6 characters long
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Display info message for host role */}
            {!isEditMode && !showPasswordField() && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md text-sm">
                Hosts do not have login access to the system. They will be
                associated with shows but cannot access any portal.
              </div>
            )}

            <div>
              <label htmlFor="role" className="block text-sm font-medium mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                id="role"
                name="roleId"
                required
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select a role</option>
                {roles
                  .filter((role) => role.id !== 5) // Exclude guest role
                  .map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name
                        .replace("_", " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </option>
                  ))}
              </select>
            </div>

            {showVenueSelection() && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Venue permissions
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-3">
                  {venues.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No venues available
                    </p>
                  ) : (
                    venues.map((venue) => (
                      <div
                        key={venue.id}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`venue-${venue.id}`}
                          checked={selectedVenueIds.includes(venue.id)}
                          onCheckedChange={() => handleVenueToggle(venue.id)}
                        />
                        <Label
                          htmlFor={`venue-${venue.id}`}
                          className="text-sm font-normal cursor-pointer"
                        >
                          {venue.name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Select venues this user can access
                </p>
              </div>
            )}

            {/* Show status for all users in edit mode */}
            {isEditMode && (
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium mb-1"
                >
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  id="status"
                  name="statusId"
                  required
                  defaultValue={user?.status_id || ""}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select status</option>
                  {statuses.length === 0 ? (
                    <option value="" disabled>
                      No statuses available
                    </option>
                  ) : (
                    statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name.charAt(0).toUpperCase() +
                          status.name.slice(1)}
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-md hover:bg-muted transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update user"
                  : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
